import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, Copy, Landmark } from 'lucide-react'
import { useState } from 'react'

import { ApiException, api } from '../../api/client'
import type { DeclareTransferBody } from '../../api/types.gen'
import { Banner, Button, Card, Field } from '../../ui'
import type { TransferInstructions } from './types'
import { formatDate, formatSoles } from './types'

/**
 * A dónde transferir, y el formulario para avisar de que ya se transfirió.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * Por qué el aviso del estudio es obligatorio y no un extra
 * ─────────────────────────────────────────────────────────────────────────────
 * Es lo que convierte «revisar el banco cada día por si acaso» en «revisarlo
 * cuando alguien avisa» (PLAN.md §12.8). Sin este paso, el coste operativo de
 * cobrar por transferencia es un barrido diario de todas las cuentas abiertas, y
 * el método deja de ser sostenible. De ahí que el formulario esté aquí mismo,
 * debajo de las cuentas, y no escondido en otra pantalla.
 *
 * El botón de copiar existe por lo mismo: el CCI son veinte dígitos que se van a
 * teclear en la app del banco, y un dígito mal copiado es una transferencia
 * devuelta.
 */
export function TransferInstructionsCard({
  instructions,
  onDeclared,
}: {
  instructions: TransferInstructions
  onDeclared?: () => void
}) {
  const queryClient = useQueryClient()
  const [operation, setOperation] = useState('')
  const [bank, setBank] = useState('')
  const [paidOn, setPaidOn] = useState('')
  const [failure, setFailure] = useState<string | null>(null)

  const declare = useMutation({
    mutationFn: () =>
      // El cuerpo va TIPADO, que es para lo que existe el segundo parámetro:
      // sin él `B = unknown` y una errata en `paid_on` no se descubre hasta el
      // 422 del servidor — o peor, hasta que el operador sigue viendo «—».
      api.post<unknown, DeclareTransferBody>('/billing/subscription/transfer/declare', {
        operation: operation.trim(),
        bank: bank.trim() || null,
        paid_on: paidOn || null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['billing'] })
      onDeclared?.()
    },
    onError: (error) => {
      setFailure(
        error instanceof ApiException ? error.message : 'No pudimos registrar tu aviso. Inténtalo otra vez.',
      )
    },
  })

  if (instructions.declared) {
    return (
      <Banner tone="accent" title="Estamos comprobando tu transferencia">
        Nos avisaste con la referencia{' '}
        <strong className="font-semibold">{instructions.reference_code}</strong>. Lo revisamos a mano
        y normalmente el mismo día hábil. En cuanto la confirmemos, tu cuenta se activa y te
        escribimos.
      </Banner>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <Card className="p-5">
        <div className="flex items-center gap-2">
          <Landmark size={18} strokeWidth={1.75} aria-hidden className="text-ink-muted" />
          <span className="text-subhead font-medium text-ink">Transfiere a una de estas cuentas</span>
        </div>

        <p className="mt-1 text-caption text-ink-subtle">
          {instructions.holder}
          {instructions.tax_id ? ` · RUC ${instructions.tax_id}` : ''}
        </p>

        <dl className="mt-4 flex flex-col gap-4">
          {instructions.accounts.map((cuenta) => (
            <div key={cuenta.cci} className="flex flex-col gap-1">
              <dt className="text-label font-medium text-ink">{cuenta.bank}</dt>
              <dd className="flex flex-col gap-1">
                <Copyable label="Cuenta" value={cuenta.number} />
                {/* El CCI no es un detalle: sin él sólo puede pagar quien tenga
                    cuenta en ese mismo banco, que es la minoría. */}
                <Copyable label="CCI" value={cuenta.cci} />
              </dd>
            </div>
          ))}
        </dl>

        <div className="mt-5 border-t border-line pt-4">
          <Copyable label="Importe exacto" value={formatSoles(instructions.amount_cents)} destacado />
          <Copyable label="Referencia" value={instructions.reference_code} destacado />
          <p className="mt-3 text-caption text-ink-subtle">
            Pon la referencia en el detalle de la transferencia y transfiere el importe exacto. La
            proforma vale hasta el {formatDate(instructions.due_at)}; después se pide otra en un clic.
          </p>
        </div>
      </Card>

      <Card className="p-5">
        <span className="text-subhead font-medium text-ink">Ya transferí</span>
        <p className="mt-1 text-body text-ink-muted">
          Dinos el número de operación. Es lo que nos pone a comprobarlo — sin este aviso puede tardar
          bastante más.
        </p>

        <div className="mt-4 flex flex-col gap-3">
          <Field
            label="Número de operación"
            value={operation}
            onChange={(event) => setOperation(event.target.value)}
            placeholder="00123456"
          />
          <Field
            label="Banco desde el que transferiste (opcional)"
            value={bank}
            onChange={(event) => setBank(event.target.value)}
            placeholder="BCP"
          />
          {/*
           | OPCIONAL, y no por descuido.
           |
           | El correo que recibe quien concilia dice «busca ese movimiento en la
           | banca por importe y fecha», y hasta el 09-09-2026 la fecha salía
           | siempre «—» porque este formulario no la preguntaba. El API la
           | aceptaba y la guardaba desde el principio.
           |
           | Por qué NO se pide obligatoria: el botón se bloquea sin el número de
           | operación, y añadir la fecha a esa condición dejaría sin poder avisar
           | a quien no recuerda el día exacto. Entonces el operador no recibe
           | NADA, que le cuesta más que un guion — y este formulario existe
           | precisamente para convertir «revisar el banco cada día» en
           | «revisarlo cuando alguien avisa».
           |
           | Y por qué NO se rellena con hoy: sería un valor plausible que puede
           | ser falso, y el operador no podría distinguir una fecha confirmada de
           | un valor por omisión. Mandarle a buscar al tramo equivocado del
           | extracto es peor que no darle tramo.
           |
           | Tampoco se valida contra nada, que es lo que ya decide el endpoint:
           | «No se valida contra la realidad —para eso está la comprobación
           | humana». Una fecha rara tiene que llegarle al operador tal cual.
           */}
          <Field
            type="date"
            label="Fecha en que transferiste (opcional)"
            hint="Nos dice en qué tramo del extracto buscar. Sin ella tardamos algo más."
            value={paidOn}
            onChange={(event) => setPaidOn(event.target.value)}
          />
        </div>

        {failure && (
          <Banner tone="danger" title="No pudimos registrarlo" live="alert" className="mt-4">
            {failure}
          </Banner>
        )}

        <Button
          className="mt-4"
          block
          disabled={operation.trim() === ''}
          busy={declare.isPending && 'Avisando…'}
          onClick={() => {
            setFailure(null)
            declare.mutate()
          }}
        >
          Avisar de mi transferencia
        </Button>
      </Card>
    </div>
  )
}

/**
 * Un dato que se va a teclear en la app del banco.
 *
 * Con botón de copiar porque un CCI son veinte dígitos y un error de
 * transcripción no se descubre hasta que el dinero vuelve.
 */
function Copyable({
  label,
  value,
  destacado = false,
}: {
  label: string
  value: string
  destacado?: boolean
}) {
  const [copiado, setCopiado] = useState(false)

  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5">
      <span className="text-caption text-ink-subtle">{label}</span>
      <span className="flex items-center gap-2">
        <span
          className={destacado ? 'text-subhead font-semibold text-ink' : 'text-body text-ink'}
          data-numeric
        >
          {value}
        </span>
        <button
          type="button"
          aria-label={`Copiar ${label}`}
          className="rounded p-1 text-ink-subtle transition-colors hover:text-ink"
          onClick={() => {
            void navigator.clipboard.writeText(value).then(() => {
              setCopiado(true)
              // Vuelve solo: un icono que se queda en «copiado» miente en cuanto
              // se copia otra cosa.
              setTimeout(() => setCopiado(false), 1500)
            })
          }}
        >
          {copiado ? (
            <Check size={15} strokeWidth={2.5} aria-hidden className="text-positive" />
          ) : (
            <Copy size={15} strokeWidth={1.75} aria-hidden />
          )}
        </button>
      </span>
    </div>
  )
}
